import React from 'react';
import axios from 'axios';
import {
	getSession,
} from 'Vulture/Session';
import {
	broncoURL,
	commitHash,
} from 'Vulture/ENV';
import CustomIcon from '../Common/CustomIcon/CustomIcon';
import style from './Chonky/ChonkyFileBrowser.module.css';

import {
	LIBRARY_EXTENSIONS,
	LIBRARY_FORMATS,
	validateFiles,
} from './Chonky/lib';

export const lowercaseExtensionInName = (name) => {
	const i = name.lastIndexOf('.');
	if (i <= 0 || i === name.length - 1) return name;
	const base = name.slice(0, i);
	const ext = name.slice(i + 1).toLowerCase();
	return `${base}.${ext}`;
};

export const withLowercasedExtension = (file) => {
	const normalizedName = lowercaseExtensionInName(file.name);
	if (normalizedName === file.name) return file;
	try {
		const newFile = new File([ file, ], normalizedName, {
			type:         file.type,
			lastModified: file.lastModified,
		});
		if (file.targetFolder) newFile.targetFolder = file.targetFolder;
		return newFile;
	} catch (e) {
		// If creating a new File fails (very old browsers), we'll rely on 'file_name' field during upload.
		return file;
	}
};

class CreateFileModal extends React.Component {
	constructor(props) {
		super(props);
		
		const initialFiles = props.initialFiles || [];
		const {
			validFiles:  initialValidFiles,
			invalidFiles: initialInvalidFiles,
		} = validateFiles(initialFiles, LIBRARY_FORMATS);
		if (initialInvalidFiles.length > 0) {
			const invalidNames = initialInvalidFiles.map((file) => {
				return file.name;
			}).join(', ');
			const validExtensions = LIBRARY_EXTENSIONS.join(', ');
			alert(`The following files have unsupported formats and will be skipped: ${invalidNames}\n\nSupported formats: ${validExtensions}`);
		}
		const validInitialFiles = initialValidFiles.map(withLowercasedExtension);
		const initialStatus = {};
		validInitialFiles.forEach((_, index) => {
			initialStatus[index] = {
				status: 'pending',
				message: '',
			};
		});

		this.state = {
			files:          validInitialFiles,
			folderNames:    props.folderNames || [],
			description:    '',
			order:          1,
			keywords:       '',
			isDragging:     false,
			fileStatus:     initialStatus,
			uploadProgress: {
				completed:  0,
				total:      0,
				inProgress: false,
			},
		};

		this.handleInputChange = this.handleInputChange.bind(this);
		this.handleFileChange = this.handleFileChange.bind(this);
		this.handleCreateFile = this.handleCreateFile.bind(this);
		this.handleClose = this.handleClose.bind(this);
		this.handleDragEnter = this.handleDragEnter.bind(this);
		this.handleDragLeave = this.handleDragLeave.bind(this);
		this.handleDragOver = this.handleDragOver.bind(this);
		this.handleDrop = this.handleDrop.bind(this);
		this.removeFile = this.removeFile.bind(this);
		this.fileInputRef = React.createRef();
		this.dragCounter = 0;
	}

	handleInputChange(e) {
		this.setState({
			[e.target.name]: e.target.value,
		});
	}

	handleFileChange(e) {
		const selectedFiles = Array.from(e.target.files);
		if (selectedFiles.length > 0) {
			const {
				validFiles,
				invalidFiles,
			} = validateFiles(selectedFiles, LIBRARY_FORMATS);

			if (invalidFiles.length > 0) {
				const invalidNames = invalidFiles.map((file) => {
					return file.name;
				}).join(', ');
				const validExtensions = LIBRARY_EXTENSIONS.join(', ');
				alert(`The following files have unsupported formats and will be skipped: ${invalidNames}\n\nSupported formats: ${validExtensions}`);
			}

			if (validFiles.length > 0) {
				const normalizedFiles = validFiles.map(withLowercasedExtension);
				const currentLength = this.state.files.length;

				const newFileStatus = {
					...this.state.fileStatus,
				};
				normalizedFiles.forEach((_, index) => {
					newFileStatus[currentLength + index] = {
						status:  'pending',
						message: '',
					};
				});

				this.setState({
					files:      [ ...this.state.files, ...normalizedFiles, ],
					fileStatus: newFileStatus,
				});
			}
		}
	}

	handleDragEnter(e) {
		e.preventDefault();
		e.stopPropagation();

		this.dragCounter++;
		if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
			this.setState({ isDragging: true });
		}
	}

	handleDragLeave(e) {
		e.preventDefault();
		e.stopPropagation();

		this.dragCounter--;
		if (this.dragCounter === 0) {
			this.setState({ isDragging: false });
		}
	}

	handleDragOver(e) {
		e.preventDefault();
		e.stopPropagation();
	}

	handleDrop(e) {
		e.preventDefault();
		e.stopPropagation();

		this.setState({
			isDragging: false,
		});
		this.dragCounter = 0;

		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			const droppedFiles = Array.from(e.dataTransfer.files);
			const {
				validFiles,
				invalidFiles,
			} = validateFiles(droppedFiles, LIBRARY_FORMATS);

			if (invalidFiles.length > 0) {
				const invalidNames = invalidFiles.map((file) => {
					return file.name;
				}).join(', ');
				const validExtensions = LIBRARY_EXTENSIONS.join(', ');
				alert(`The following files have unsupported formats and will be skipped: ${invalidNames}\n\nSupported formats: ${validExtensions}`);
			}

			if (validFiles.length > 0) {
				const normalizedFiles = validFiles.map(withLowercasedExtension);
				const currentLength = this.state.files.length;

				const newFileStatus = {
					...this.state.fileStatus,
				};
				normalizedFiles.forEach((_, index) => {
					newFileStatus[currentLength + index] = {
						status:  'pending',
						message: '',
					};
				});

				this.setState({
					files:      [ ...this.state.files, ...normalizedFiles ],
					fileStatus: newFileStatus,
				});
			}
			e.dataTransfer.clearData();
		}
	}

	removeFile(index) {
		const updatedFiles = [ ...this.state.files, ];
		updatedFiles.splice(index, 1);

		const updatedFileStatus = {};
		Object.entries(this.state.fileStatus).forEach(([ oldIndex, status, ]) => {
			const numIndex = parseInt(oldIndex);
			if (numIndex < index) {
				updatedFileStatus[numIndex] = status;
			} else if (numIndex > index) {
				updatedFileStatus[numIndex - 1] = status;
			}
		});

		this.setState({
			files:      updatedFiles,
			fileStatus: updatedFileStatus,
		});
	}

	async handleCreateFile() {
		const {
			currentFolderId,
			refreshFileBrowserData,
			app,
			accountUUID,
		} = this.props;

		const {
			files,
			description,
			keywords,
			order,
		} = this.state;

		if (files.length === 0) {
			alert('Please select at least one file to upload');
			return;
		}

		const filesToUpload = files.filter((_, index) => {
			const status = this.state.fileStatus[index]?.status;
			return status !== 'success';
		});

		if (filesToUpload.length === 0) {
			alert('All files have already been uploaded successfully');
			return;
		}

		this.setState({
			uploadProgress: {
				completed:  0,
				total:      filesToUpload.length,
				inProgress: true,
			},
		});

		app.setState({
			loading: true,
		});

		try {
			const session = getSession();
			const headers = {
				headers: {
					'X-Auth-Session':      session.session,
					'X-Spark-API-version': commitHash,
				},
			};

			const folderIdMap = {};
			if (this.state.folderNames && this.state.folderNames.length > 0) {
				for (const fName of this.state.folderNames) {
					let folderUrl;
					if (currentFolderId === 'root') {
						folderUrl = `${broncoURL}/api/folder/1/folder`;
					} else {
						const backendFolderId = currentFolderId.replace('node_', '');
						folderUrl = `${broncoURL}/api/folder/${backendFolderId}/folder?account_uuid=${accountUUID}`;
					}
					
					let createdFolderId;
					try {
						const response = await axios.post(
							folderUrl,
							{ name: fName },
							{ ...headers, headers: { ...headers.headers, 'Content-Type': 'application/json' } }
						);
						if (response.status === 200 && response.data) {
							createdFolderId = response.data.id;
						}
					} catch (folderErr) {
						console.error(`Failed to create folder ${fName}`, folderErr);
						const msg = folderErr?.response?.data?.message || folderErr.message || String(folderErr);
						throw new Error(`Failed to create folder "${fName}": ${msg}`);
					}

					// A 200 without an id leaves folderIdMap incomplete, which later
					// surfaces as a misleading per-file "Failed to create target folder".
					if (!createdFolderId) {
						throw new Error(`Failed to create folder "${fName}": server returned no folder id`);
					}
					folderIdMap[fName] = createdFolderId;
				}
			}

			for (let i = 0; i < files.length; i++) {
				if (this.state.fileStatus[i]?.status === 'success') {
					continue;
				}

				const file = files[i];
				const normalizedName = lowercaseExtensionInName(file.name);
				const normalizedFile = withLowercasedExtension(file);

				let url;
				if (file.targetFolder) {
					const targetFolderId = folderIdMap[file.targetFolder];
					if (!targetFolderId) {
						this.setState((prevState) => {
							return {
								fileStatus: {
									...prevState.fileStatus,
									[i]: {
										status:  'error',
										message: `Failed to create target folder: ${file.targetFolder}`,
									},
								},
							};
						});
						continue;
					}
					url = `${broncoURL}/api/folder/${targetFolderId}/file?account_uuid=${accountUUID}`;
				} else if (currentFolderId === 'root') {
					url = `${broncoURL}/api/folder/1/file`;
				} else {
					const backendFolderId = currentFolderId.replace('node_', '');
					url = `${broncoURL}/api/folder/${backendFolderId}/file?account_uuid=${accountUUID}`;
				}

				this.setState((prevState) => {
					return {
						fileStatus: {
							...prevState.fileStatus,
							[i]: {
								status:  'uploading',
								message: 'Uploading...',
							},
						},
					};
				});

				try {
					const formData = new FormData();
					formData.append('file', normalizedFile);
					formData.append('file_name', normalizedName);
					formData.append('order', order);
					if (description) formData.append('description', description);
					if (keywords) formData.append('keywords', keywords);


					await axios.post(url, formData, headers);

					this.setState((prevState) => {
						return {
							fileStatus: {
								...prevState.fileStatus,
								[i]: {
									status:  'success',
									message: 'Upload complete',
								},
							},
							uploadProgress: {
								...prevState.uploadProgress,
								completed: prevState.uploadProgress.completed + 1,
							},
						};
					});
				} catch (err) {
					this.setState((prevState) => {
						return {
							fileStatus: {
								...prevState.fileStatus,
								[i]: {
									status:  'error',
									message: err.response?.data?.message || err.message || 'Upload failed'
								},
							},
						};
					});
				}
			}

			await refreshFileBrowserData();

			this.setState((prevState) => {
				return {
					uploadProgress: {
						...prevState.uploadProgress,
						inProgress: false,
					},
				};
			});

			const allSuccess = Object.values(this.state.fileStatus).every((status) => {
				return status.status === 'success';
			});

			if (allSuccess) {
				app.closeModal();
				this.setState({
					files: [],
					description: '',
					keywords: '',
					fileStatus: {}
				});
			}
		} catch (err) {
			console.error('File upload error:', err);
			alert(`Error uploading files: ${err.response?.data?.message || err.message}`);
			this.setState(prevState => ({
				uploadProgress: {
					...prevState.uploadProgress,
					inProgress: false
				}
			}));
		} finally {
			app.setState({ loading: false });
		}
	}

	handleClose() {
		const { app } = this.props;
		app.closeModal();
	}

	getStatusIcon(status) {
		switch (status) {
		case 'pending':
			return <CustomIcon icon='file' />;
		case 'uploading':
			return <CustomIcon icon='cloud-upload-alt' />;
		case 'success':
			return <CustomIcon icon='check-circle' />;
		case 'error':
			return <CustomIcon icon='exclamation-circle' />;
		default:
			return <CustomIcon icon='file' />;
		}
	}

	render() {
		const {
			isVCLEnabled,
		} = this.props;

		const {
			isDragging,
			files,
			fileStatus,
			uploadProgress,
			order,
		} = this.state;

		const pendingFiles = [];
		const successFiles = [];
		const errorFiles = [];

		files.forEach((file, index) => {
			const status = fileStatus[index]?.status || 'pending';
			if (status === 'success') {
				successFiles.push({ file, index, status: fileStatus[index] });
			} else if (status === 'error') {
				errorFiles.push({ file, index, status: fileStatus[index] });
			} else {
				pendingFiles.push({ file, index, status: fileStatus[index] });
			}
		});

		const renderFileItem = (fileInfo) => {
			return (
				<li key={fileInfo.index} className={`${style.fileItem} ${fileInfo.status?.status === 'error' ? style.errorItem : ''}`}>
					{this.getStatusIcon(fileInfo.status?.status)}
					<span>{fileInfo.file.name}</span>
					{fileInfo.status?.status === 'error' && (
						<span className={style.errorMessage}>
							{fileInfo.status.message}
						</span>
					)}
					<button
						className={style.removeFile}
						onClick={(e) => {
							e.stopPropagation();
							this.removeFile(fileInfo.index);
						}}
						disabled={fileInfo.status?.status === 'uploading'}
					>
						<CustomIcon icon='times' />
					</button>
				</li>
			);
		};

		return (
			<div className={style.chonkyModal}>
				<div
					className={`${style.formGroup} ${style.fileDropArea} ${isDragging ? style.dragging : ''}`}
					onDragEnter={this.handleDragEnter}
					onDragLeave={this.handleDragLeave}
					onDragOver={this.handleDragOver}
					onDrop={this.handleDrop}
					onClick={() => this.fileInputRef.current.click()}
				>
					<div className={style.fileDropContent}>
						{files.length > 0 ? (
							<div className={style.selectedFiles}>
								<p>
									{files.length} file(s) selected (
									{(() => {
										const totalBytes = files.reduce((acc, f) => acc + (f.size || 0), 0);
										const i = totalBytes === 0 ? 0 : Math.floor(Math.log(totalBytes) / Math.log(1024));
										return (totalBytes / Math.pow(1024, i)).toFixed(2) + ' ' + ['B', 'KB', 'MB', 'GB', 'TB'][i];
									})()})
								</p>
								
								{this.state.folderNames && this.state.folderNames.length > 0 && (
									<p style={{ color: 'var(--ts-brand-primary, #005A8C)', marginTop: '5px' }}>
										Uploading into new folder(s): {this.state.folderNames.join(', ')}
									</p>
								)}

								{uploadProgress.inProgress && (
									<div className={style.uploadProgressContainer}>
										<div className={style.uploadProgressBar}>
											<div
												className={style.uploadProgressFill}
												style={{
													width: `${(uploadProgress.completed / uploadProgress.total) * 100}%`
												}}
											></div>
										</div>
										<p className={style.uploadProgressText}>
											Uploading: {uploadProgress.completed} of {uploadProgress.total} complete
										</p>
									</div>
								)}

								{pendingFiles.length > 0 && (
									<>
										<h4>Files Pending - please click the Upload Files button</h4>
										<ul className={style.fileList}>
											{pendingFiles.map(renderFileItem)}
										</ul>
									</>
								)}

								{errorFiles.length > 0 && (
									<>
										<h4>Failed</h4>
										<ul className={style.fileList}>
											{errorFiles.map(renderFileItem)}
										</ul>
									</>
								)}

								{successFiles.length > 0 && (
									<>
										<h4>Successful</h4>
										<ul className={style.fileList}>
											{successFiles.map(renderFileItem)}
										</ul>
									</>
								)}
							</div>
						) : (
							<>
								<CustomIcon icon='upload' className={style.uploadIcon} />
								<p>{isDragging ? 'Drop files here' : 'Drag and drop files here or click to browse'}</p>
								<p style={{ fontSize: '0.9em', color: '#666', marginTop: '8px' }}>
									Supported formats: {LIBRARY_EXTENSIONS.join(', ').toUpperCase()}
								</p>
							</>
						)}
						<input
							type="file"
							data-test-id="libraryFileInput"
							onChange={this.handleFileChange}
							ref={this.fileInputRef}
							style={{ display: 'none' }}
							accept={LIBRARY_EXTENSIONS.join(',')}
							multiple
						/>
					</div>
				</div>

				<div className={style.formGroup}>
					<label>
						<span className={style.labelWithHelp}>
							Descriptive Name
							<span className={style.settingHelpIcon}>
								<CustomIcon icon='question-circle' />
								<span className={style.settingHelpToolTip}>{`Descriptive name for identification. Defaults to the file name when left empty.`}</span>
							</span>
						</span>
						<input
							type="text"
							name="description"
							value={this.state.description}
							onChange={this.handleInputChange}
							placeholder="Brief file description (optional)"
						/>
					</label>
				</div>

				<div className={style.formGroup}>
					<label>
						<span className={style.labelWithHelp}>
							<span className={`${style.aiIndicator} ${this.state.keywords ? style.aiIndicatorInactive : style.aiIndicatorActive}`}>
								<CustomIcon icon='magic' />
								<span className={style.settingHelpToolTip}>{`Leave empty to have keywords auto-generated. ${this.state.keywords ? 'Will not be AI generated.' : 'Will be AI generated.'}`}</span>
							</span>
							Keywords
							<span className={style.settingHelpIcon}>
								<CustomIcon icon='question-circle' />
								<span className={style.settingHelpToolTip}>{`Comma-separated keywords used for asset search.`}</span>
							</span>
						</span>
						<input
							type="text"
							name="keywords"
							value={this.state.keywords}
							onChange={this.handleInputChange}
							placeholder="Comma-separated keywords (optional)"
						/>
					</label>
				</div>

				{isVCLEnabled && <div className={`text-small ${style.order}`}>
					<div className={`label ${style.label}`}>
						<label>
							<span className={style.labelWithHelp}>
								Order
								<span className={style.settingHelpIcon}>
									<CustomIcon icon='question-circle' />
									<span className={style.settingHelpToolTip}>{`Display order in visual content loops. Lower numbers appear first.`}</span>
								</span>
							</span>
						</label>
					</div>
					<input
						value={order}
						id='modalOrder'
						type='number'
						placeholder='1'
						onChange={(e) => {
							const nextOrder = Number(e.target.value.trim())
							this.setState({
								order: nextOrder >= 1 ? nextOrder : 1,
							});
						}}
						onBlur={(e) => {
							const nextOrder = Number(e.target.value.trim())
							this.setState({
								order: nextOrder >= 1 ? nextOrder : 1,
							});
						}}
					/>
				</div>}

				<div className={style.buttonContainer}>
					<button
						onClick={this.handleCreateFile}
						data-test-id="uploadFilesConfirmButton"
						className={`button ${files.length === 0 && 'button-disabled'}`}
						disabled={
							files.length === 0 ||
							Object.values(fileStatus).some(status => status?.status === 'uploading') ||
							Object.values(fileStatus).every(status => status?.status === 'success')
						}
					>
						<CustomIcon icon='upload' />
						{errorFiles.length > 0 ? 'Retry Failed Uploads' : `Upload File${files.length > 1 ? 's' : ''}`}
					</button>

					<button
						onClick={this.handleClose}
						className={`button secondary`}
						disabled={Object.values(fileStatus).some(status => status?.status === 'uploading')}
					>
						<CustomIcon icon='times' />
						Close
					</button>
				</div>
			</div>
		);
	}
}

export default CreateFileModal;